import { useCallback, useMemo } from 'react';
import MojitoCore from 'mojito/core';
import { isEmpty } from 'mojito/utils';
import BannerPromotionsView from './view/index.js';
import {
    usePromotions,
    usePromotionsPreload,
    usePromotionsLoadDone,
} from 'modules/promotions/hooks';
import { useLoadingStatusMetric } from 'modules/performance/hooks';
import { useDispatch } from 'react-redux';
import PromotionUtils from 'modules/promotions/utils/promotion-utils.js';

const intentActions = MojitoCore.Intents.actions;
const { ContextExtender: AnalyticsContextExtender, ContextBuilder: AnalyticsContextBuilder } =
    MojitoCore.Presentation.AnalyticsContext;

const analyticsContext = new AnalyticsContextBuilder().withAnalyticsPath('Banner').build();

export default function BannerPromotions(props) {
    const {
        applicablePage,
        mojitoTools: { config, instanceId, appContext },
    } = props;
    const { locationId, maxNumberOfPromotions, reportLoadingStatus } = config;
    const { analyticsEmitter } = appContext;
    const promotions = usePromotions(locationId, applicablePage);
    const dispatch = useDispatch();
    const promotionsLoadDone = usePromotionsLoadDone();

    const activePromotions = useMemo(
        () =>
            maxNumberOfPromotions >= 0 ? promotions.slice(0, maxNumberOfPromotions) : promotions,
        [maxNumberOfPromotions, promotions]
    );

    const [onPrepareBannersToRender, eventsLoadDone, marketsLoadDone] = usePromotionsPreload(
        activePromotions,
        promotionsLoadDone,
        instanceId
    );

    const noContent = promotionsLoadDone && isEmpty(activePromotions);

    const onBannerClick = useCallback(
        promotion => {
            const intent = PromotionUtils.resolveIntentFromPromotion(promotion);
            if (intent) {
                dispatch(intentActions.publishIntent(intent.type, intent.data));
            }
            analyticsEmitter.emitAnalytics('promotionClicked', {
                locationId,
                bannerName: promotion.linkText,
            });
        },
        [locationId, dispatch, analyticsEmitter]
    );

    const performanceElementRef = useLoadingStatusMetric(
        promotionsLoadDone || noContent,
        reportLoadingStatus ? instanceId : undefined,
        'BannerPromotions',
        noContent
    );

    if (noContent) {
        return null;
    }

    const promotionsPreloadDone = eventsLoadDone && marketsLoadDone;

    return (
        <AnalyticsContextExtender value={analyticsContext}>
            <BannerPromotionsView
                promotions={activePromotions}
                promotionsLoaded={promotionsLoadDone}
                config={config.view}
                onPrepareBannersToRender={onPrepareBannersToRender}
                onBannerClick={onBannerClick}
                shouldRequestData={promotionsPreloadDone}
                elementRef={performanceElementRef}
            />
        </AnalyticsContextExtender>
    );
}
